import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, User, Mail, Eye, Heart } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const PrivacyEN = () => {
  const navigate = useNavigate();

  const handleDonateClick = () => {
    navigate('/en#donate');
    setTimeout(() => {
      const element = document.getElementById('donate');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };
  return (
    <div className="min-h-screen bg-warm-beige pt-32 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/en"
              className="inline-flex items-center space-x-2 text-olive-green hover:text-olive-dark transition-colors font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Homepage</span>
            </Link>
          </motion.div>

          {/* Datenschutz Content */}
          <motion.div
            className="bg-white rounded-2xl shadow-xl p-12 border border-olive-green/10"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-8">
              <Shield className="w-8 h-8 text-olive-green" />
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-charcoal font-heading break-words">
                Privacy Policy
              </h1>
            </div>

            <div className="prose prose-lg max-w-none">
              <div className="bg-olive-green/10 p-6 rounded-xl border-l-4 border-olive-green mb-8">
                <p className="text-lg text-charcoal/80 leading-relaxed">
                  We take the protection of your data very seriously. We treat personal data (e.g., name, email, IP address) 
                  confidentially and in accordance with legal data protection regulations.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-charcoal mb-4 font-heading flex items-center space-x-2">
                  <User className="w-6 h-6 text-olive-green" />
                  <span>Responsible Party</span>
                </h2>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-charcoal/80 leading-relaxed">
                    <strong>hay - humanity. action. you. e.V.</strong><br />
                    <a href="mailto:info@hayforhumanity.de" className="text-olive-green hover:text-olive-dark transition-colors">
                      info@hayforhumanity.de
                    </a>
                  </p>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-charcoal mb-4 font-heading">
                  1. Data Collection on this Website
                </h2>
                <div className="space-y-4">
                  <p className="text-charcoal/80 leading-relaxed">
                    When visiting our website, data such as IP address, browser type, and time are automatically stored.
                  </p>
                  <p className="text-charcoal/80 leading-relaxed">
                    If you contact us via the contact form or by email, we only process the data entered there 
                    to handle your inquiry.
                  </p>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-charcoal mb-4 font-heading">
                  2. Cookies and Analysis Tools
                </h2>
                <p className="text-charcoal/80 leading-relaxed">
                  Our website may use cookies to ensure functionality. Our website uses SSL/TLS encryption for security reasons. We do not use tracking or advertising cookies.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-charcoal mb-4 font-heading">
                  3. Embedded Content and External Links
                </h2>
                <div className="space-y-4">
                  <p className="text-charcoal/80 leading-relaxed">
                    Our website does not use embedded content that automatically transmits data to third-party providers. 
                    However, we link to external sites such as PayPal, WhatsApp, Instagram, or other websites (e.g., source references).
                  </p>
                  <p className="text-charcoal/80 leading-relaxed">
                    If you follow one of these links, the privacy policies of the respective provider apply. 
                    We have no influence on how external services process your data.
                  </p>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-charcoal mb-4 font-heading">
                  4. Rights of Data Subjects
                </h2>
                <p className="text-charcoal/80 leading-relaxed mb-4">
                  Within the framework of legal requirements, you have various rights regarding your personal data. These include in particular:
                </p>
                <ul className="list-disc list-inside space-y-2 text-charcoal/80 mb-4">
                  <li>Right to information about stored personal data</li>
                  <li>Right to correction of inaccurate data</li>
                  <li>Right to deletion of data, provided there are no legal retention obligations</li>
                  <li>Right to restriction of processing in legally provided cases</li>
                </ul>
                <p className="text-charcoal/80 leading-relaxed italic">
                  These rights exist by law – they do not need to be actively exercised unless there is reason to do so.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-charcoal mb-4 font-heading">
                  Privacy Contact
                </h2>
                <p className="text-charcoal/80 leading-relaxed">
                  If you have questions about the handling of your data, you can contact us at any time:<br />
                  <a href="mailto:info@hayforhumanity.de" className="text-olive-green font-semibold hover:text-olive-dark transition-colors">info@hayforhumanity.de</a>
                </p>
              </div>

              {/* Additional Info */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <p className="text-charcoal/70 text-sm leading-relaxed">
                  This privacy policy complies with the current legal requirements of the GDPR. 
                  We reserve the right to change this privacy policy and will inform accordingly on this page.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Donate CTA */}
          <motion.div
            className="mt-8 bg-gradient-to-r from-olive-green to-olive-dark rounded-2xl p-8 text-white text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Heart className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-4 font-heading">
              Support our mission.
            </h3>
            <p className="text-lg text-white/90 mb-6 leading-relaxed max-w-2xl mx-auto">
              Every donation helps us support people in need.
            </p>
            <button
              onClick={handleDonateClick}
              className="bg-white text-olive-green hover:bg-cream px-8 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-xl"
            >
              Donate now
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyEN;